import { AlertTriangle } from 'lucide-react';
import { useForm } from 'react-hook-form';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { CustomRadioGroup } from '@/modules/common';
import { InputFieldWithCurrency } from '@/modules/common/components/molecules/inputs/input-field-with-currency';
import { ProjectStatus } from '@/modules/projects/types/project';

export interface StatusOption {
  value: ProjectStatus;
  label: string;
  isItemDisabled?: boolean;
}

export interface ProjectStatusSectionProps {
  form: ReturnType<typeof useForm<any>>;
  isReadMode: boolean;
  statusOptions: StatusOption[];
  showWarning: boolean;
  currentStatus: ProjectStatus;
  t: (key: string) => string;
}

/**
 * Status selection section with conditional probability field and warnings.
 * Handles status transitions and business rule validations.
 */
export function ProjectStatusSection({
  form,
  isReadMode,
  statusOptions,
  showWarning,
  currentStatus,
  t,
}: ProjectStatusSectionProps) {
  return (
    <div className="space-y-3">
      <CustomRadioGroup
        control={form.control}
        name="status"
        disabled={isReadMode}
        label={t('common.status')}
        options={statusOptions}
      />

      {showWarning && (
        <Alert variant="warning">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{t('details.tabs.info.statusChangeWarning')}</AlertDescription>
        </Alert>
      )}

      {currentStatus === ProjectStatus.Tentative && (
        <InputFieldWithCurrency
          name="probability"
          label={t('details.tabs.info.probability')}
          placeholder={t('details.tabs.info.probabilityPlaceholder')}
          control={form.control}
          suffixText="%"
          max={100}
          isReadMode={isReadMode}
        />
      )}
    </div>
  );
}
